import React, { useEffect, useMemo, useState, useRef } from "react";
import {
  Box,
  Paper,
  Stack,
  Typography,
  CircularProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import axios from "axios";
import EventIcon from "@mui/icons-material/Event";
import GroupsIcon from "@mui/icons-material/Groups";
import BeachAccessIcon from "@mui/icons-material/BeachAccess";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import { styled } from "@mui/material/styles";
import Zoom from "@mui/material/Zoom";
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import "jspdf-autotable";

/* ---------- Helpers ---------- */

function renderStatus(val) {
  const s = typeof val === "string" ? val.toLowerCase() : val;
  if (val === 1 || s === "posted")
    return <Chip label="Posted" size="small" color="success" sx={{ fontWeight: 700 }} />;
  if (val === 2 || s === "machine")
    return <Chip label="Machine" size="small" color="info" sx={{ fontWeight: 700 }} />;
  if (val === 3 || s === "site")
    return <Chip label="Site" size="small" color="warning" sx={{ fontWeight: 700 }} />;
  if (s === "weekend")
    return <Chip label="Weekend" size="small" variant="outlined" sx={{ fontWeight: 700 }} />;
  if (s === "vacation")
    return <Chip label="Vacation" size="small" color="error" sx={{ fontWeight: 700 }} />;
  return <span style={{ opacity: 0.6 }}>—</span>;
}

function formatTimeRange(a, b) {
  const t1 = a || null;
  const t2 = b || null;
  if (t1 && t2) return `${t1} ~ ${t2}`; // normalize to tilde between times
  if (t1) return t1;
  if (t2) return t2;
  return "";
}



/** Compute per-employee row stats */
function getRowStats(emp, monthDays) {
  const vacDays = monthDays.reduce((n, iso) => n + (emp.days[iso]?.vacation ? 1 : 0), 0);
  const attendanceDays = monthDays.reduce((n, iso) => {
    const c = emp.days[iso];
    if (!c) return n;
    const s = Number(c.status);
    const present = Boolean(c.time) || s === 1 || s === 2 || s === 3;
    return present && !c.vacation ? n + 1 : n;
  }, 0);
  return { vacDays, attendanceDays };
}

/** Stylish, animated tooltip for first column only */
const SoftTooltip = styled(({ className, ...props }) => (
  <Tooltip
    {...props}
    arrow
    enterDelay={200}
    leaveDelay={60}
    TransitionComponent={Zoom}
    slotProps={{ popper: { className } }}
    classes={{ popper: className }}
  />
))(() => ({
  ["& .MuiTooltip-tooltip"]: {
    background: "linear-gradient(180deg, rgba(224,242,241,.96) 0%, rgba(255,255,255,.94) 100%)",
    backdropFilter: "blur(6px)",
    WebkitBackdropFilter: "blur(6px)",
    color: "#003d33",
    border: "1px solid rgba(178,223,219,.9)",
    borderRadius: 14,
    boxShadow: "0 18px 50px rgba(0,0,0,.18)",
    padding: "12px 14px",
    fontSize: 13,
    fontWeight: 700,
    maxWidth: 360,
  },
  ["& .MuiTooltip-arrow"]: {
    color: "rgba(224,242,241,.96)",
    "&::before": {
      boxShadow: "inset 0 0 0 1px rgba(178,223,219,.9)",
    },
  },
}));

// Weekend helper (Sun=0, Sat=6). Adjust if needed.
const WEEKEND_DAYS = new Set([0, 6]);
const isWeekendIso = (iso) => WEEKEND_DAYS.has(dayjs(iso).day());

const normalizeBaseUrl = (raw) => {
  if (!raw) return "";
  const trimmed = raw.trim().replace(/\/$/, "");
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
};

/* ---------- Row issue detection helpers ---------- */

// Accepts "08:44 ~ 16:02", "08:44 - 16:02", "08:44 – 16:02", "08:44 — 16:02"
const TIME_RANGE_RE = /^\s*\d{2}:\d{2}\s*[~\-–—]\s*\d{2}:\d{2}\s*$/;

// Consider these as "ignored" placeholders (no issue)
const isDashOnly = (v) => {
  const s = String(v ?? "").trim();
  return s === "-" || s === "—";
};

/* ---------- Component ---------- */

export default function AllAttendancePage() {
  const [month, setMonth] = useState(dayjs().startOf("month"));
  const [loading, setLoading] = useState(false);
  const [monthData, setMonthData] = useState({});
  const [search, setSearch] = useState("");
  const [hideWeekends, setHideWeekends] = useState(true); // default: hide

  // Column hover state
  const [hoverIso, setHoverIso] = useState(null);

  // Drag-to-scroll
  const containerRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const dragStateRef = useRef({ startX: 0, scrollLeft: 0 });

  // Cursor change while wheel/trackpad horizontal scroll
  const [isScrolling, setIsScrolling] = useState(false);

  const onContainerMouseDown = (e) => {
    if (!containerRef.current || e.button !== 0) return; // left button only
    setIsDragging(true);
    dragStateRef.current = {
      startX: e.pageX - containerRef.current.offsetLeft,
      scrollLeft: containerRef.current.scrollLeft,
    };
  };

  const onContainerMouseMove = (e) => {
    if (!isDragging || !containerRef.current) return;
    e.preventDefault(); // prevents text selection while dragging
    const x = e.pageX - containerRef.current.offsetLeft;
    const walk = x - dragStateRef.current.startX; // positive when moving right
    containerRef.current.scrollLeft = dragStateRef.current.scrollLeft - walk;
  };

  const endDrag = () => setIsDragging(false);

  // listen to scroll to toggle isScrolling (for cursor feedback)
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    let timeoutId;
    let lastLeft = el.scrollLeft;

    const onScroll = () => {
      const movedHorizontally = el.scrollLeft !== lastLeft;
      lastLeft = el.scrollLeft;
      if (movedHorizontally) {
        setIsScrolling(true);
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => setIsScrolling(false), 400);
      }
    };

    el.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      el.removeEventListener("scroll", onScroll);
      clearTimeout(timeoutId);
    };
  }, []);

  // Build list of ISO dates in the selected month
  const monthDays = useMemo(() => {
    const start = month.startOf("month");
    const days = start.daysInMonth();
    return Array.from({ length: days }, (_, i) => start.add(i, "day").format("YYYY-MM-DD"));
  }, [month]);

  // Visible days (apply weekend filter)
  const visibleDays = useMemo(
    () => (hideWeekends ? monthDays.filter((iso) => !isWeekendIso(iso)) : monthDays),
    [monthDays, hideWeekends]
  );

  const normalizedSearch = useMemo(() => search.trim().toLowerCase(), [search]);

  // Totals
  const allEmployees = useMemo(() => Object.values(monthData).map((e) => e.employee), [monthData]);
  const totalEmployees = allEmployees.length;

  const vacationCount = useMemo(() => {
    return Object.values(monthData).filter((emp) =>
      Object.values(emp.days).some((d) => d?.vacation === true)
    ).length;
  }, [monthData]);

  // Filtered keys
  const filteredKeys = useMemo(() => {
    const term = normalizedSearch;
    if (!term) return Object.keys(monthData);
    return Object.entries(monthData)
      .filter(([key, emp]) => {
        const hay = `${emp.employee ?? ""} ${key ?? ""}`.toLowerCase();
        return hay.includes(term);
      })
      .map(([key]) => key);
  }, [monthData, normalizedSearch]);

const handleExportPDF = () => {
  const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });

  // Title
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.text(`Attendance — ${month.format("YYYY MMM")}`, 40, 32);

  // Build table head: Employee + day numbers
  const head = [
    ["Employee", ...visibleDays.map((iso) => dayjs(iso).format("DD"))]
  ];

  // Build body rows
  const body = filteredKeys.map((empKey) => {
    const emp = monthData[empKey];
    const row = [emp.employee];

    visibleDays.forEach((iso) => {
      const cell = emp.days[iso];
      const vacLabel =
        cell?.vacation === true
          ? (cell.vacationType || emp.vacationType || "Vacation")
          : null;

      const val = vacLabel
        ? vacLabel
        : (cell?.time || (cell ? cell.status : "") || "-");

      row.push(String(val));
    });

    return row;
  });

  // AutoTable
  doc.autoTable({
    head,
    body,
    startY: 50,
    styles: {
      font: "helvetica",
      fontSize: 8,
      cellPadding: 4,
      overflow: "linebreak",
      valign: "middle",
    },
    headStyles: {
      fillColor: [0, 121, 107], // teal header
      textColor: 255,
      fontStyle: "bold",
    },
    columnStyles: {
      0: { cellWidth: 160, fontStyle: "bold" }, // Employee column wider
    },
    // Let AutoTable manage paging automatically
    didDrawPage: (data) => {
      // Footer
      const pageSize = doc.internal.pageSize;
      const pageWidth = pageSize.getWidth();
      const pageHeight = pageSize.getHeight();
      doc.setFontSize(8);
      doc.setFont("helvetica", "normal");
      doc.text(
        `Generated: ${dayjs().format("YYYY-MM-DD HH:mm")}`,
        40,
        pageHeight - 16
      );
      doc.text(
        `Page ${doc.internal.getNumberOfPages()}`,
        pageWidth - 80,
        pageHeight - 16
      );
    },
  });

  doc.save(`attendance_${month.format("YYYY_MM")}.pdf`);
};

  const handleExportExcel = () => {
  // Flatten data: [ { Employee, Day1, Day2, ... } ]
  const rows = filteredKeys.map((empKey) => {
    const emp = monthData[empKey];
    const row = { Employee: emp.employee };
    visibleDays.forEach((iso) => {
      const cell = emp.days[iso];
      const vacLabel =
        cell?.vacation === true
          ? cell.vacationType || emp.vacationType || "Vacation"
          : null;

      row[iso] = vacLabel
        ? vacLabel
        : cell?.time || (cell ? cell.status : "") || "-";
    });
    return row;
  });

  // Create worksheet + workbook
  const worksheet = XLSX.utils.json_to_sheet(rows);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Attendance");

  // Download file
  XLSX.writeFile(workbook, `attendance_${month.format("YYYY_MM")}.xlsx`);
};

  useEffect(() => {
    let cancelled = false;

    const fetchMonth = async () => {
      setLoading(true);
      try {
        const rawBase = localStorage.getItem("saved_sett_api_url");
        const API_BASE_URL = normalizeBaseUrl(rawBase);
        if (!API_BASE_URL) {
          setMonthData({});
          return;
        }

        // Fetch each day of the month in parallel
        const requests = monthDays.map((isoDate) =>
          axios
            .get(`${API_BASE_URL}/api/attendances`, {
              params: { date: isoDate },
              withCredentials: true,
            })
            .then((res) => ({ date: isoDate, data: res.data || [] }))
            .catch(() => ({ date: isoDate, data: [] }))
        );

        const results = await Promise.all(requests);
        if (cancelled) return;

        const agg = {};
        results.forEach(({ date, data }) => {
          data.forEach((item, idx) => {
            const key = item.empCode ?? item.empName ?? `emp-${idx}`;
            if (!agg[key]) {
              agg[key] = {
                employee: item.empName ?? item.empCode ?? key,
                days: {},
              };
            }

            const vacType = item.vacationtype ?? item.vacationType ?? null;
            if (vacType && !agg[key].vacationType) {
              agg[key].vacationType = vacType;
            }

            agg[key].days[date] = {
              time: formatTimeRange(item.timeIn ?? null, item.timeOut ?? null),
              status: item.status ?? null,
              vacation:
                item.vacation ??
                (vacType ? true : String(item.status || "").toLowerCase() === "vacation"),
              vacationType: vacType,
            };
          });
        });

        setMonthData(agg);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchMonth();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-start",
        pt: 4,
        px: 2,
        background: {
          xs: "linear-gradient(180deg, #e0f2f1 0%, #ffffff 40%)",
          md: "linear-gradient(135deg, #e0f2f1 0%, #ffffff 55%)",
        },
      }}
    >
      <Paper
        elevation={10}
        sx={{
          width: "100%",
          maxWidth: 1400,
          p: 3,
          borderRadius: 4,
          overflow: "hidden",
          background: "linear-gradient(180deg, #ffffff 0%, #fbfffe 100%)",
          boxShadow: "0 20px 60px rgba(0,0,0,.12)",
          border: "1px solid #b2dfdb",
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 2 }}
          spacing={1.5}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <EventIcon sx={{ color: "#00796b" }} />
            <Typography variant="h6" fontWeight={800} color="#004d40">
              All Attendance (Monthly)
            </Typography>
          </Stack>

          <Stack direction="row" spacing={1}>
            <Chip
              icon={<GroupsIcon />}
              label={`Employees: ${totalEmployees}`}
              color="primary"
              variant="outlined"
              sx={{ borderColor: "#00796b", color: "#004d40", fontWeight: 700 }}
            />
            <Chip
              icon={<BeachAccessIcon />}
              label={`Any Vacation: ${vacationCount}`}
              color="warning"
              variant="filled"
              sx={{ fontWeight: 700 }}
            />
          </Stack>
        </Stack>

        {/* Filters */}
        <Box
          sx={{
            mb: 2.5,
            p: 2,
            bgcolor: "#f1fdfb",
            border: "1px solid #b2dfdb",
            borderRadius: 2,
          }}
        >
          <Typography variant="subtitle2" sx={{ color: "#00796b", mb: 1 }}>
            Filter by Month & Search Employees
          </Typography>

          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems="center"
            flexWrap="wrap"
          >
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Month"
                views={["year", "month"]}
                openTo="month"
                value={month}
                onChange={(newVal) => setMonth((newVal || dayjs()).startOf("month"))}
                slotProps={{
                  textField: {
                    size: "small",
                    sx: { backgroundColor: "white", borderRadius: 2, minWidth: 220 },
                  },
                }}
              />
            </LocalizationProvider>

            <TextField
              label="Search employees"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              size="small"
              placeholder="Type name or code…"
              sx={{ backgroundColor: "white", borderRadius: 2, minWidth: 260 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: search ? (
                  <InputAdornment position="end">
                    <IconButton size="small" aria-label="clear search" onClick={() => setSearch("")}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />

            {/* Hide/show weekends toggle */}
            <FormControlLabel
              control={
                <Checkbox checked={hideWeekends} onChange={(e) => setHideWeekends(e.target.checked)} />
              }
              label="Hide weekends"
              sx={{ ml: { md: 1 } }}
            />
          </Stack>
        </Box>
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mb: 1 }}>
          <Chip
            label="Export to Excel"
            color="success"
            variant="outlined"
            onClick={handleExportExcel}
            sx={{ cursor: "pointer", fontWeight: 700 }}
          />
          <Chip
            label="Export to PDF"
            color="primary"
            variant="outlined"
            onClick={handleExportPDF}
            sx={{ cursor: "pointer", fontWeight: 700 }}
          />
        </Box>
        {/* Table */}
        <Paper elevation={3} sx={{ borderRadius: 2, overflow: "auto", maxWidth: "100%" }}>
          {loading ? (
            <Box sx={{ display: "grid", placeItems: "center", py: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer
              ref={containerRef}
              onMouseDown={onContainerMouseDown}
              onMouseMove={onContainerMouseMove}
              onMouseUp={endDrag}
              onMouseLeave={endDrag}
              sx={{
                maxHeight: "70vh",
                cursor: isDragging ? "grabbing" : isScrolling ? "ew-resize" : "grab",
                userSelect: isDragging ? "none" : "auto",
                scrollBehavior: isDragging ? "auto" : "smooth",
              }}
            >
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        position: "sticky",
                        left: 0,
                        zIndex: 3,
                        backgroundColor: "#004d40",
                        color: "white",
                        fontWeight: "bold",
                        minWidth: 180,
                        borderRight: "1px solid #004d40",
                      }}
                    >
                      Employee
                    </TableCell>
                    {visibleDays.map((iso) => {
                      const d = dayjs(iso);
                      const weekend = isWeekendIso(iso);
                      return (
                        <TableCell
                          key={iso}
                          onMouseEnter={() => setHoverIso(iso)}
                          onMouseLeave={() => setHoverIso(null)}
                          sx={{
                            textAlign: "center",
                            backgroundColor: weekend ? "#00695c" : "#00796b",
                            color: "white",
                            fontWeight: "bold",
                            minWidth: 90,
                            borderLeft: "1px solid #004d40",
                            boxShadow:
                              hoverIso === iso ? "inset 0 -9999px rgba(0,0,0,0.06)" : "none",
                            transition: "box-shadow .15s",
                          }}
                        >
                          <div style={{ lineHeight: 1.1 }}>
                            <div>{d.format("DD")}</div>
                            <div style={{ fontSize: 11, opacity: 0.9 }}>{d.format("ddd")}</div>
                          </div>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {filteredKeys.map((empKey) => {
                    const emp = monthData[empKey];
                    const { vacDays, attendanceDays } = getRowStats(emp, monthDays);

                    // Row-level issue detection (checks only visibleDays)
                    const hasIssue = visibleDays.some((iso) => {
                      const cell = emp.days[iso];
                      if (!cell) return false;                 // missing -> dash, ignore
                      if (isDashOnly(cell.time)) return false;  // explicit dash -> ignore
                      if (cell.time) return !TIME_RANGE_RE.test(cell.time);
                      return true; // any non-dash, non-time content -> issue
                    });

                    // ---- UPDATED: Incomplete cells count ----
                    // Count ONLY cells that have a time string and do NOT match the expected pattern.
                    // Ignore: missing cells, dash-only cells, and any non-time content (e.g., status/vacation).
                    const incompleteCount = visibleDays.reduce((n, iso) => {
                      const cell = emp.days[iso];
                      if (!cell) return n; // missing -> renders dash, ignore
                      const t = cell.time;
                      if (!t || isDashOnly(t)) return n; // no time or dash -> ignore
                      return TIME_RANGE_RE.test(t) ? n : n + 1; // count only bad time formats
                    }, 0);

                    return (
                      <TableRow
                        key={empKey}
                        hover
                        sx={{
                          "& td": {
                            fontSize: { xs: 12, sm: 13 },
                            py: 0.75,
                            px: 1,
                            verticalAlign: "middle",
                          },
                        }}
                      >
                        {/* Tooltip ONLY on first (Employee) column */}
                        <TableCell
                          sx={{
                            position: "sticky",
                            left: 0,
                            zIndex: 2,
                            backgroundColor: hasIssue ? "#fdecea" : "#e0f2f1",
                            fontWeight: 600,
                            color: "#004d40",
                            "&:nth-of-type(odd)": {
                              backgroundColor: hasIssue ? "#fdecea" : "#f1fdfb",
                            },
                            whiteSpace: "nowrap",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: 1,
                            }}
                          >
                            <SoftTooltip
                              placement="right"
                              title={
                                <Stack spacing={0.5}>
                                  <Typography variant="body2">Vacation days: {vacDays}</Typography>
                                  <Typography variant="body2">Attendance days: {attendanceDays}</Typography>
                                  <Typography variant="body2">
                                    Incomplete attendance: {incompleteCount}
                                  </Typography>
                                </Stack>
                              }
                            >
                              <span style={{ cursor: "help" }}>{emp.employee}</span>
                            </SoftTooltip>

                            {incompleteCount > 0 && (
                              <Chip
                                label={incompleteCount}
                                size="small"
                                sx={{
                                  height: 20,
                                  fontSize: 12,
                                  fontWeight: 700,
                                  "& .MuiChip-label": { px: 1 },
                                  backgroundColor: "rgba(151, 21, 21, 0.06)", // light red background
                                  color: "#971515", // dark red text
                                }}
                                title="Incomplete time cells (visible days)"
                              />
                            )}
                          </Box>
                        </TableCell>

                        {visibleDays.map((iso) => {
                          const weekend = isWeekendIso(iso);
                          const cell = emp.days[iso];

                          if (!cell) {
                            return (
                              <TableCell
                                key={iso}
                                onMouseEnter={() => setHoverIso(iso)}
                                onMouseLeave={() => setHoverIso(null)}
                                sx={{
                                  textAlign: "center",
                                  backgroundColor:
                                    hoverIso === iso ? "rgba(0, 121, 107, 0.06)" : "transparent",
                                  transition: "background-color .12s",
                                }}
                              >
                                {weekend ? renderStatus("weekend") : (
                                  <span style={{ opacity: 0.6 }}>—</span>
                                )}
                              </TableCell>
                            );
                          }

                          const vacLabel =
                            cell.vacation === true
                              ? cell.vacationType || emp.vacationType || "Vacation"
                              : null;

                          const content = vacLabel ? (
                            <span style={{ fontWeight: 700, color: "#d32f2f" }}>
                              {vacLabel}
                            </span>
                          ) : (
                            cell.time || renderStatus(cell.status || (weekend ? "weekend" : null))
                          );

                          return (
                            <TableCell
                              key={iso}
                              onMouseEnter={() => setHoverIso(iso)}
                              onMouseLeave={() => setHoverIso(null)}
                              sx={{
                                textAlign: "center",
                                backgroundColor:
                                  hoverIso === iso ? "rgba(0, 121, 107, 0.06)" : "transparent",
                                transition: "background-color .12s",
                              }}
                            >
                              {content}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );
                  })}

                  {!filteredKeys.length && (
                    <TableRow>
                      <TableCell
                        colSpan={1 + visibleDays.length}
                        sx={{ textAlign: "center", py: 3, opacity: 0.7 }}
                      >
                        No matching employees
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Paper>
    </Box>
  );
}
